import { ProjectData } from "grapesjs";

export const enum CustomCommands {
  MODAL_SELECT_TEMPLATES_OPEN = "modal:select-templates:open",
  MODAL_SELECT_TEMPLATES_CLOSE = "modal:select-templates:close",

  MODAL_OPEN_GRAPH_OPEN = "modal:open-graph:open",
  MODAL_OPEN_GRAPH_SAVE = "modal:open-graph:save",
  MODAL_OPEN_GRAPH_CLOSE = "modal:open-graph:close",

  MODAL_FAVICON_OPEN = "modal:favicons:open",
  MODAL_FAVICON_SAVE = "modal:favicons:save",
  MODAL_FAVICON_CLOSE = "modal:favicons:close",
  
  MODAL_CUSTOM_CSS_OPEN = "modal:custom-css:open",
  MODAL_CUSTOM_CSS_SAVE = "modal:custom-css:save",
  MODAL_CUSTOM_CSS_CLOSE = "modal:custom-css:close",

  MODAL_JOBITEM_TEMPLATE_OPEN = "modal:jobitem-template:open",
  MODAL_JOBITEM_TEMPLATE_SAVE = "modal:jobitem-template:save",
  MODAL_JOBITEM_TEMPLATE_CLOSE = "modal:jobitem-template:close",
  
  MODAL_EXTERNAL_RESOURCES_OPEN = "modal:external-resources:open",
  MODAL_EXTERNAL_RESOURCES_CLOSE = "modal:external-resources:close",
  MODAL_EXTERNAL_RESOURCES_SAVE = "modal:external-resources:save",
  
  TEMPLATE_PUBLISH_OPEN = "template:publish:open",
  TEMPLATE_PUBLISH_SAVE = "template:publish:save",
  TEMPLATE_PUBLISH_CLOSE = "template:publish:close",

  TEMPLATE_OPEN_PREVIEW = "template:open:preview",
  TEMPLATE_OPEN_LIVE = "template:open:live",

  ASSET_MANAGER_OPEN = "asset-manager:open",
  ASSET_MANAGER_CLOSE = "asset-manager:close",

  MODAL_REVISIONS_OPEN = "modal:revisions:open",
  MODAL_REVISIONS_CLOSE = "modal:revisions:close",

  MODAL_FONT_SELECTOR_OPEN = "modal:font-selector:open",
  MODAL_FONT_SELECTOR_CLOSE = "modal:font-selector:close",

  MODAL_CUSTOM_FONT_OPEN = "modal:custom-font:open",
  MODAL_CUSTOM_FONT_CLOSE = "modal:custom-font:close",

  GET_FONT_LINKS = "get-font-links",
}

export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return function (...args: Parameters<T>): void {
    if (timeout) clearTimeout(timeout);
    // @ts-ignore
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

/**
 * Like `debounce`, but the returned function resolves/rejects a Promise with the
 * trailing call's result. Lets callers (e.g. GrapesJS `Storage.store`) await the
 * debounced async work and learn whether it actually succeeded, instead of the
 * plain `debounce` which returns `void` and silently swallows failures.
 */
export function debounceAsync<A extends any[], R>(
  func: (...args: A) => Promise<R>,
  wait: number
): (...args: A) => Promise<R> {
  let timeout: NodeJS.Timeout | null = null;
  let waiters: { resolve: (value: R) => void; reject: (reason: unknown) => void }[] =
    [];
  return function (this: unknown, ...args: A): Promise<R> {
    if (timeout) clearTimeout(timeout);
    return new Promise<R>((resolve, reject) => {
      waiters.push({ resolve, reject });
      timeout = setTimeout(() => {
        const current = waiters;
        waiters = [];
        Promise.resolve(func.apply(this, args)).then(
          (value) => current.forEach((w) => w.resolve(value)),
          (reason) => current.forEach((w) => w.reject(reason))
        );
      }, wait);
    });
  };
}

export const getExternalResources = (data: ProjectData) => {
  const javascript: string[] = data?.dataSources
      ?.find((source: any) => source.id === "external-resources")
      ?.records.find((record: any) => record.id === "javascript")
      ?.value.map((item: any) => item.url) || [];

  const css: string[] = 
    data?.dataSources
      ?.find((source: any) => source.id === "external-resources")
      ?.records.find((record: any) => record.id === "css")
      ?.value.map((item: any) => item.url) || [];

  return { javascript, css };
}